import { sumBy } from 'lodash';
import { queryClient } from '../../../../config/queryClient';
import { useAssetBucketingColors } from '../../../FinancialPlanOPL/Goals/pages/AssetBucketingResults/hooks/useAssetBucketingColors';
import { buildBucketData } from '../../../FinancialPlanOPL/Goals/pages/AssetBucketingResults/useAssetBucketingResults';
import { useTheme } from '../../../theme/theme';
import { queryKeys } from './api/queryKeys';
import { AssetBucketingDTO } from './api/useAssetBucketQuery';
import { getBucketChartSizesDict } from '../../../FinancialPlanOPL/Goals/pages/AssetBucketingResults/utils/getBucketChartSizesDict';

const ASSET_VALUE = 100_000;

export const useAssetBucketingPreview = () => {
  const theme = useTheme();

  const bucketColors = useAssetBucketingColors();

  const data = queryClient.getQueryData<AssetBucketingDTO>(queryKeys.assetBucket());

  if (!data) {
    ADV.util.Router.go2('AdviserSettings', { page: 'AssetBucketing' });

    return null;
  }

  const { headline, supportingText, isDefaultAssetBucketingLayout } = data;

  const networthMakeup = data.buckets.map((bucket, i) => ({
    id: bucket.title,
    label: bucket.title,
    value: bucket.classifications.length * ASSET_VALUE,
    color: bucketColors[i][0],
  }));

  const networthAmount = sumBy(data.buckets, bucket => bucket.classifications.length * ASSET_VALUE);

  const chartSizesDict = getBucketChartSizesDict(
    data.buckets.map(({ title, classifications }) => ({
      title,
      items: classifications.map(() => ({ value: ASSET_VALUE })),
    })),
  );

  const buckets = data.buckets.map((bucket, i) =>
    buildBucketData({
      colors: bucketColors[i],
      items: bucket.classifications.map(asset => ({
        name: asset.description,
        value: ASSET_VALUE,
        assets: [],
        transactions: [],
        holdings: [],
        displayValue: ASSET_VALUE,
        networthValue: ASSET_VALUE,
        isHoldingsBucketInheritedFromAsset: false,
        chartValue: 0,
      })),
      description: bucket.description,
      mainColor: bucketColors[i][0],
      title: bucket.title,
      otherAssetColor: theme.colors.neutral[400],
      total: bucket.classifications.length * ASSET_VALUE,
      chartSizesDict,
      chartTotal: bucket.classifications.length * ASSET_VALUE,
    }),
  );

  const navigateToSettings = async () => {
    queryClient.setQueryDefaults(queryKeys.assetBucket(), {
      cacheTime: 0,
    });

    queryClient.setQueryData(queryKeys.assetBucket(), data);

    ADV.util.Router.go2('AdviserSettings', { page: 'AssetBucketing' });
  };

  return {
    data: {
      headline,
      supportingText,
      networthAmount,
      networthMakeup,
      buckets,
      isDefaultAssetBucketingLayout,
    },
    actions: {
      navigateToSettings,
    },
  };
};
